import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable, signal } from '@angular/core';
import { map, Observable, tap } from 'rxjs';
import { environment } from '../../environments/environment';
import {
  ApiListWrapper,
  PortalAccessOption,
  PortalListRow,
  PortalRecordQuery,
  PortalOverview,
  PortalShipmentDashboard,
  PortalShipmentListItem,
  PortalShipmentWorkspace,
  PortalShipmentTracking,
  PortalShipmentQuery,
  PortalQuotationListItem,
  PortalQuotationWorkspace,
  PortalSupportTicket,
  PortalSupportTicketSummary,
  CreatePortalSupportTicket,
  PortalPosmWorkspace,
  PortalSession,
  PortalWarehousingFilters,
  PortalWarehousingItem,
  PortalWarehousingItemPage,
  PortalWarehousingLedgerPage,
  PortalWarehousingStockMovement,
  PortalWarehousingStockReport,
  PortalItemMovementsPage,
  PortalItemMovementJourney,
} from './portal.models';

@Injectable({ providedIn: 'root' })
export class PortalApiService {
  readonly session = signal<PortalSession | null>(null);

  constructor(private readonly http: HttpClient) {}

  loadSession(): Observable<PortalSession> {
    return this.http
      .get<PortalSession>(`${environment.restApiUrl}customer-portal/session/me`)
      .pipe(tap((session) => this.session.set(session)));
  }

  loadAccessOptions(): Observable<PortalAccessOption[]> {
    return this.http
      .get<PortalAccessOption[]>(`${environment.restApiUrl}customer-portal/session/access-options`)
      .pipe(
        map((options) =>
          (options ?? []).map((option) => ({
            ...option,
            roleNames: option.roleNames ?? [],
            permissions: option.permissions ?? [],
          })),
        ),
      );
  }

  clearSession(): void {
    this.session.set(null);
  }

  hasPermission(permission: string): boolean {
    return !!permission && (this.session()?.permissions ?? []).includes(permission);
  }

  list(
    path: string,
    page = 0,
    pageSize = 20,
    query: PortalRecordQuery = {},
  ): Observable<ApiListWrapper<PortalListRow>> {
    let params = new HttpParams().set('page', page).set('pageSize', pageSize);
    Object.entries(query).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        params = params.set(key, String(value));
      }
    });
    return this.http
      .get<ApiListWrapper<PortalListRow>>(`${environment.restApiUrl}customer-portal/${path}`, {
        params,
      })
      .pipe(
        map((response) => ({
          ...response,
          items: response.items ?? [],
          count: response.count ?? 0,
          limit: response.limit ?? pageSize,
          offset: response.offset ?? page * pageSize,
          hasMore: !!response.hasMore,
        })),
      );
  }

  getPosmProjects(page = 0, pageSize = 20): Observable<ApiListWrapper<PortalListRow>> {
    return this.list('posm/projects', page, pageSize);
  }

  getShipmentDashboard(): Observable<PortalShipmentDashboard> {
    return this.http.get<PortalShipmentDashboard>(
      `${environment.restApiUrl}customer-portal/shipments/dashboard`,
    );
  }

  getOverview(): Observable<PortalOverview> {
    return this.http.get<PortalOverview>(`${environment.restApiUrl}customer-portal/overview`);
  }

  getShipments(
    page = 0,
    pageSize = 12,
    query: PortalShipmentQuery = {},
  ): Observable<ApiListWrapper<PortalShipmentListItem>> {
    let params = new HttpParams().set('page', page).set('pageSize', pageSize);
    Object.entries(query).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        params = params.set(key, String(value));
      }
    });
    return this.http
      .get<ApiListWrapper<PortalShipmentListItem>>(
        `${environment.restApiUrl}customer-portal/shipments`,
        { params },
      )
      .pipe(
        map((response) => ({
          ...response,
          items: response.items ?? [],
          count: response.count ?? 0,
          limit: response.limit ?? pageSize,
          offset: response.offset ?? page * pageSize,
          hasMore: !!response.hasMore,
        })),
      );
  }

  getShipmentWorkspace(shipmentId: number): Observable<PortalShipmentWorkspace> {
    return this.http.get<PortalShipmentWorkspace>(
      `${environment.restApiUrl}customer-portal/shipments/${shipmentId}`,
    );
  }

  getShipmentTracking(shipmentId: number, deliveryId: number): Observable<PortalShipmentTracking> {
    return this.http.get<PortalShipmentTracking>(
      `${environment.restApiUrl}customer-portal/shipments/${shipmentId}/tracking`,
      { params: { deliveryId } },
    );
  }

  exportShipments(): Observable<Blob> {
    return this.http.get(`${environment.restApiUrl}customer-portal/shipments/export`, {
      responseType: 'blob',
    });
  }

  getQuotations(page = 0, pageSize = 50): Observable<ApiListWrapper<PortalQuotationListItem>> {
    return this.http
      .get<ApiListWrapper<PortalQuotationListItem>>(
        `${environment.restApiUrl}customer-portal/quotations`,
        {
          params: { page, pageSize },
        },
      )
      .pipe(
        map((response) => ({
          ...response,
          items: response.items ?? [],
          count: response.count ?? 0,
          limit: response.limit ?? pageSize,
          offset: response.offset ?? page * pageSize,
          hasMore: !!response.hasMore,
        })),
      );
  }

  getQuotationWorkspace(quotationId: number): Observable<PortalQuotationWorkspace> {
    return this.http.get<PortalQuotationWorkspace>(
      `${environment.restApiUrl}customer-portal/quotations/${quotationId}`,
    );
  }

  addQuotationComment(quotationId: number, message: string): Observable<PortalQuotationWorkspace> {
    return this.http.post<PortalQuotationWorkspace>(
      `${environment.restApiUrl}customer-portal/quotations/${quotationId}/comments`,
      { message },
    );
  }

  approveQuotation(quotationId: number, comment?: string): Observable<PortalQuotationWorkspace> {
    return this.http.post<PortalQuotationWorkspace>(
      `${environment.restApiUrl}customer-portal/quotations/${quotationId}/approve`,
      { comment },
    );
  }

  getSupportTickets(): Observable<PortalSupportTicketSummary[]> {
    return this.http.get<PortalSupportTicketSummary[]>(
      `${environment.restApiUrl}customer-portal/support/tickets`,
    );
  }

  getSupportTicket(ticketId: number): Observable<PortalSupportTicket> {
    return this.http.get<PortalSupportTicket>(
      `${environment.restApiUrl}customer-portal/support/tickets/${ticketId}`,
    );
  }

  createSupportTicket(ticket: CreatePortalSupportTicket): Observable<PortalSupportTicket> {
    return this.http.post<PortalSupportTicket>(
      `${environment.restApiUrl}customer-portal/support/tickets`,
      ticket,
    );
  }

  addSupportComment(ticketId: number, body: string): Observable<PortalSupportTicket> {
    return this.http.post<PortalSupportTicket>(
      `${environment.restApiUrl}customer-portal/support/tickets/${ticketId}/comments`,
      { body },
    );
  }

  uploadSupportAttachment(ticketId: number, file: File): Observable<PortalSupportTicket> {
    const body = new FormData();
    body.append('file', file);
    return this.http.post<PortalSupportTicket>(
      `${environment.restApiUrl}customer-portal/support/tickets/${ticketId}/attachments`,
      body,
    );
  }

  downloadSupportAttachment(ticketId: number, attachmentId: number): Observable<Blob> {
    return this.http.get(
      `${environment.restApiUrl}customer-portal/support/tickets/${ticketId}/attachments/${attachmentId}/download`,
      { responseType: 'blob' },
    );
  }

  getPosmWorkspace(projectId: number): Observable<PortalPosmWorkspace> {
    return this.http.get<PortalPosmWorkspace>(
      `${environment.restApiUrl}customer-portal/posm/projects/${projectId}`,
    );
  }

  exportPosmProjects(): Observable<Blob> {
    return this.http.get(`${environment.restApiUrl}customer-portal/posm/projects/export`, {
      responseType: 'blob',
    });
  }

  exportPosmWorkspace(projectId: number): Observable<Blob> {
    return this.http.get(
      `${environment.restApiUrl}customer-portal/posm/projects/${projectId}/export`,
      {
        responseType: 'blob',
      },
    );
  }

  getWarehouseStock(
    filters?: PortalWarehousingFilters,
    view: 'ITEM' | 'UNIT' = 'ITEM',
  ): Observable<PortalWarehousingStockReport> {
    return this.http
      .get<{ data?: PortalWarehousingStockReport }>(
        `${environment.restApiUrl}customer-portal/warehousing/stock-report`,
        { params: this.filterParams(filters).set('view', view) },
      )
      .pipe(
        map((response) => ({
          totals: response.data?.totals,
          groupedRows: response.data?.groupedRows ?? [],
          detailRows: response.data?.detailRows ?? [],
          availableDimensions: response.data?.availableDimensions ?? [],
          warehouseOptions: response.data?.warehouseOptions ?? [],
          conditionOptions: response.data?.conditionOptions ?? [],
          statusOptions: response.data?.statusOptions ?? [],
        })),
      );
  }

  getWarehouseItems(
    keyword?: string | null,
    page = 0,
    pageSize = 50,
  ): Observable<PortalWarehousingItemPage> {
    let params = new HttpParams().set('page', page).set('pageSize', pageSize);
    if (keyword?.trim()) {
      params = params.set('keyword', keyword.trim());
    }
    return this.http
      .get<{ data?: PortalWarehousingItemPage }>(
        `${environment.restApiUrl}customer-portal/warehousing/items`,
        { params },
      )
      .pipe(
        map((response) => ({
          items: response.data?.items ?? [],
          totalRecords: response.data?.totalRecords ?? 0,
          page: response.data?.page ?? page,
          size: response.data?.size ?? pageSize,
        })),
      );
  }

  getWarehouseItem(itemId: number): Observable<PortalWarehousingItem> {
    return this.http
      .get<{ data?: PortalWarehousingItem }>(
        `${environment.restApiUrl}customer-portal/warehousing/items/${itemId}`,
      )
      .pipe(map((response) => response.data ?? { id: itemId }));
  }

  getWarehouseMovements(
    filters?: PortalWarehousingFilters,
  ): Observable<PortalWarehousingStockMovement[]> {
    return this.http
      .get<{ data?: PortalWarehousingStockMovement[] }>(
        `${environment.restApiUrl}customer-portal/warehousing/stock-movements`,
        { params: this.filterParams(filters) },
      )
      .pipe(map((response) => response.data ?? []));
  }

  getWarehouseLedger(
    filters: PortalWarehousingFilters = {},
    page = 0,
    pageSize = 25,
  ): Observable<PortalWarehousingLedgerPage> {
    const params = this.filterParams(filters).set('page', page).set('pageSize', pageSize);
    return this.http
      .get<{ data?: PortalWarehousingLedgerPage }>(
        `${environment.restApiUrl}customer-portal/warehousing/item-transactions`,
        { params },
      )
      .pipe(
        map((response) => ({
          rows: response.data?.rows ?? [],
          summary: response.data?.summary,
          page: response.data?.page ?? page,
          size: response.data?.size ?? pageSize,
          totalElements: response.data?.totalElements ?? 0,
          totalPages: response.data?.totalPages ?? 0,
          movementFrom: response.data?.movementFrom,
          movementTo: response.data?.movementTo,
          reconciliationWarning: !!response.data?.reconciliationWarning,
        })),
      );
  }

  exportWarehouseStock(
    filters?: PortalWarehousingFilters,
    format: 'xlsx' | 'csv' = 'xlsx',
  ): Observable<Blob> {
    return this.http.post(
      `${environment.restApiUrl}customer-portal/warehousing/stock-report/export`,
      this.normalizedFilters(filters),
      {
        params: new HttpParams().set('format', format),
        responseType: 'blob',
      },
    );
  }

  exportWarehouseStockAudit(filters?: PortalWarehousingFilters): Observable<Blob> {
    return this.http.post(
      `${environment.restApiUrl}customer-portal/warehousing/stock-report/audit-export`,
      this.normalizedFilters(filters),
      { responseType: 'blob' },
    );
  }

  exportWarehouseMovements(
    filters?: PortalWarehousingFilters,
    format: 'xlsx' | 'csv' = 'xlsx',
  ): Observable<Blob> {
    return this.http.post(
      `${environment.restApiUrl}customer-portal/warehousing/stock-movements/export`,
      this.normalizedFilters(filters),
      {
        params: new HttpParams().set('format', format),
        responseType: 'blob',
      },
    );
  }

  exportWarehouseLedger(
    filters?: PortalWarehousingFilters,
    format: 'xlsx' | 'csv' = 'xlsx',
  ): Observable<Blob> {
    return this.http.post(
      `${environment.restApiUrl}customer-portal/warehousing/item-transactions/export`,
      this.normalizedFilters(filters),
      {
        params: new HttpParams().set('format', format),
        responseType: 'blob',
      },
    );
  }

  getItemMovements(
    filters: PortalWarehousingFilters = {},
    page = 0,
    pageSize = 25,
  ): Observable<PortalItemMovementsPage> {
    const params = this.filterParams(filters).set('page', page).set('pageSize', pageSize);
    return this.http
      .get<{ data?: PortalItemMovementsPage }>(
        `${environment.restApiUrl}customer-portal/warehousing/item-movements`,
        { params },
      )
      .pipe(
        map((response) => response.data ?? {
          rows: [],
          metrics: {
            totalTrackedItems: 0,
            activeInStores: 0,
            inWarehouse: 0,
            inTransit: 0,
            totalDeployments: 0,
            uniqueStoresServed: 0,
          },
          page,
          size: pageSize,
          totalElements: 0,
          totalPages: 0,
        }),
      );
  }

  getItemJourney(itemId: number, stockUnitId?: number): Observable<PortalItemMovementJourney> {
    let params = new HttpParams();
    if (stockUnitId) {
      params = params.set('stockUnitId', stockUnitId);
    }
    return this.http
      .get<{ data?: PortalItemMovementJourney }>(
        `${environment.restApiUrl}customer-portal/warehousing/items/${itemId}/journey`,
        { params },
      )
      .pipe(map((response) => response.data!));
  }

  exportItemMovements(
    filters?: PortalWarehousingFilters,
    format: 'xlsx' | 'csv' = 'xlsx',
  ): Observable<Blob> {
    return this.http.post(
      `${environment.restApiUrl}customer-portal/warehousing/item-movements/export`,
      this.normalizedFilters(filters),
      {
        params: new HttpParams().set('format', format),
        responseType: 'blob',
      },
    );
  }

  private filterParams(filters?: PortalWarehousingFilters): HttpParams {
    let params = new HttpParams();
    const normalized = this.normalizedFilters(filters);

    Object.entries(normalized).forEach(([key, value]) => {
      if (value === null || value === undefined || value === '') {
        return;
      }

      if (Array.isArray(value)) {
        value.forEach((entry) => {
          if (entry !== null && entry !== undefined && entry !== '') {
            params = params.append(key, String(entry));
          }
        });
        return;
      }

      params = params.set(key, String(value));
    });

    return params;
  }

  private normalizedFilters(filters?: PortalWarehousingFilters): PortalWarehousingFilters {
    if (!filters) {
      return {};
    }

    return {
      ...filters,
      keyword: filters.keyword?.trim() || null,
      referenceType: filters.referenceType?.trim() || null,
      referenceNumber: filters.referenceNumber?.trim() || null,
    };
  }
}
